import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { History, CheckCircle2 } from "lucide-react";

const ICONS = {
  booking_created: { dot: "bg-emerald-500", l: "Booking created" },
  booking_updated: { dot: "bg-blue-500", l: "Booking updated" },
  booking_confirmed: { dot: "bg-emerald-500", l: "Customer confirmed" },
  booking_cancelled: { dot: "bg-red-500", l: "Cancelled" },
  booking_no_show: { dot: "bg-red-500", l: "Marked no-show" },
  booking_returned: { dot: "bg-emerald-500", l: "Vehicle returned" },
  booking_warning_override: { dot: "bg-amber-500", l: "Warning override saved" },
  booking_vehicle_reassigned: { dot: "bg-blue-500", l: "Vehicle reassigned" },
  booking_picked_up: { dot: "bg-emerald-500", l: "Marked picked up" },
  pickup_handover_started: { dot: "bg-blue-500", l: "Pickup handover started" },
  pickup_handover_completed: { dot: "bg-emerald-500", l: "Pickup handover completed" },
  pickup_handover_completed_no_odometer: { dot: "bg-amber-500", l: "Pickup handover completed (no odometer)" },
  pickup_handover_corrected: { dot: "bg-amber-500", l: "Pickup handover corrected" },
  inspection_created: { dot: "bg-blue-500", l: "Inspection started" },
  inspection_finalized: { dot: "bg-emerald-500", l: "Inspection finalized" },
  deposit_collected: { dot: "bg-emerald-500", l: "Deposit collected" },
  deposit_refunded: { dot: "bg-blue-500", l: "Deposit refunded" },
  deposit_withheld: { dot: "bg-red-500", l: "Deposit withheld" },
  deposit_disputed: { dot: "bg-purple-500", l: "Dispute opened" },
  deposit_dispute_resolved: { dot: "bg-emerald-500", l: "Dispute resolved" },
  deposit_proof_uploaded: { dot: "bg-blue-400", l: "Proof uploaded" },
  deposit_proof_deleted: { dot: "bg-slate-400", l: "Proof deleted" },
  deposit_review_required: { dot: "bg-amber-500", l: "Deposit review recommended" },
  deposit_requested: { dot: "bg-blue-400", l: "Deposit requested" },
  deposit_refund_pending: { dot: "bg-amber-500", l: "Refund marked pending" },
  terms_version_published: { dot: "bg-teal-500", l: "Terms version published" },
  warning_action: { dot: "bg-amber-500", l: "Warning action" },
  warning_customer_notified: { dot: "bg-blue-500", l: "Customer notified" },
  vehicle_qr_rotated: { dot: "bg-blue-500", l: "Vehicle QR code rotated" },
  handover_vehicle_verified: { dot: "bg-emerald-500", l: "Vehicle verified at pickup" },
  handover_verification_blocked: { dot: "bg-red-500", l: "Pickup verification blocked" },
  handover_attempted_handover_reported: { dot: "bg-amber-500", l: "Attempted handover reported" },
};

function fmt(dt) {
  if (!dt) return "-";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

/** Human-friendly activity feed for a single booking. */
export default function BookingTimeline({ bookingId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}/timeline`);
        setItems(data.items || []);
      } catch (_) { /* hide on permission */ }
      finally { setLoading(false); }
    })();
  }, [bookingId]);

  if (loading) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="booking-timeline-card">
      <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2">
        <History className="w-4 h-4 text-[#1B4332]" /> Activity timeline
      </h3>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 mt-3" data-testid="timeline-empty">
          No activity recorded yet.
        </div>
      ) : (
        <ol className="mt-3 relative border-l border-slate-200 ml-2 pl-4 space-y-3"
             data-testid="timeline-list">
          {items.slice().reverse().map((e) => {
            const meta = ICONS[e.action_type] || { dot: "bg-slate-400", l: e.action_type };
            return (
              <li key={e.id} className="text-sm relative"
                  data-testid={`timeline-item-${e.id}`}>
                <span className={`absolute -left-[22px] top-1 w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-semibold text-slate-900">{meta.l}</span>
                  <span className="text-xs text-slate-500">{fmt(e.created_at)}{e.by_name ? ` · ${e.by_name}` : ""}</span>
                </div>
                {e.description && <div className="text-slate-700 text-xs mt-0.5">{e.description}</div>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
